// Public certificate verification page – accessible without authentication
import { useEffect, useState } from 'react'
import { CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react'

import { getCertificateStatus } from '@/features/classes/stores'
import type { Certificate } from '@/features/classes/stores'
import { fetchCourseBySlug } from '@/core/api/coursesService'
import { cn } from '@/core/lib/utils'

import { fmtDate } from '../utils/formatters'
import { getCertVerification } from '../api/certificatesService'

type VerifyResult =
  | { found: true; status: 'valid' | 'expired' | 'revoked'; cert: Certificate; name: string; course: string }
  | { found: false }

interface PublicVerifyPageProps {
  token: string
}

export function PublicVerifyPage({ token }: PublicVerifyPageProps) {
  const [result, setResult] = useState<VerifyResult | null>(null)
  const [verifying, setVerifying] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function verify() {
      setVerifying(true)
      try {
        const row = await getCertVerification(token)
        if (cancelled) {return}
        if (!row) {
          setResult({ found: false })
          return
        }

        const cert: Certificate = {
          id: '',
          attendeeId: '', // not exposed by the public endpoint
          certificateNumber: row.certificateNumber,
          issuedAt: row.issuedAt,
          expiresAt: row.expiresAt,
          verificationToken: token,
          revoked: false, // revoked certificates are filtered out by the backend
        }

        const first = row.firstName?.trim()
        const last = row.lastName?.trim()
        const name = [first, last].filter(Boolean).join(' ') || 'Unknown'
        // Prefer the course name from the API; fall back to the raw course id
        let courseName = row.courseId ?? 'Unknown'
        try {
          const course = await fetchCourseBySlug(row.courseId)
          if (course?.name) {courseName = course.name}
        } catch {
          // Catalog unavailable — keep the fallback
        }

        if (!cancelled) {
          const status = getCertificateStatus(cert)
          setResult({ found: true, status, cert, name, course: courseName })
        }
      } catch {
        if (!cancelled) {setResult({ found: false })}
      } finally {
        if (!cancelled) {setVerifying(false)}
      }
    }

    void verify()
    return () => { cancelled = true }
  }, [token])

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Brand header */}
        <div className="flex flex-col items-center gap-3">
          <img
            src="/images/logo/ub-logo-128.webp"
            alt="UBSafety"
            className="w-14 h-14 object-contain"
          />
          <div className="text-center">
            <h1 className="text-xl font-bold text-navy">UBSafety</h1>
            <p className="text-sm text-slate-500">Verificación de Certificado</p>
          </div>
        </div>

        {/* Result card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-150 overflow-hidden">
          {verifying ? (
            <div className="py-12 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm">Verificando certificado…</p>
            </div>
          ) : !result?.found ? (
            <div className="py-10 px-6 flex flex-col items-center gap-3 text-center">
              <XCircle className="w-10 h-10 text-red-400" />
              <p className="font-semibold text-red-700">Certificado no encontrado</p>
              <p className="text-sm text-slate-500">
                Ningún certificado coincide con este token de verificación.
              </p>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {/* Status banner */}
              <div
                className={cn(
                  'flex items-center gap-3 rounded-xl p-4',
                  result.status === 'valid'
                    ? 'bg-green-50 border border-green-200'
                    : result.status === 'expired'
                    ? 'bg-yellow-50 border border-yellow-200'
                    : 'bg-red-50 border border-red-200'
                )}
              >
                {result.status === 'valid' ? (
                  <CheckCircle2 className="w-7 h-7 text-green-600 shrink-0" />
                ) : result.status === 'expired' ? (
                  <Clock className="w-7 h-7 text-yellow-600 shrink-0" />
                ) : (
                  <XCircle className="w-7 h-7 text-red-600 shrink-0" />
                )}
                <div>
                  <p
                    className={cn(
                      'font-bold text-lg',
                      result.status === 'valid'
                        ? 'text-green-700'
                        : result.status === 'expired'
                        ? 'text-yellow-700'
                        : 'text-red-700'
                    )}
                  >
                    {result.status === 'valid'
                      ? 'Certificado Vigente'
                      : result.status === 'expired'
                      ? 'Certificado Vencido'
                      : 'Certificado Revocado'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {result.status === 'valid'
                      ? 'Este certificado es válido y auténtico.'
                      : result.status === 'expired'
                      ? 'Este certificado ha expirado. Se requiere renovación.'
                      : 'Este certificado ha sido revocado y ya no es válido.'}
                  </p>
                </div>
              </div>

              {/* Certificate details */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-slate-500">Estudiante</span>
                  <span className="font-semibold text-navy">{result.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-slate-500">Curso</span>
                  <span className="font-semibold text-navy text-right max-w-[60%]">{result.course}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-slate-500">No. Certificado</span>
                  <span className="font-mono text-xs font-medium text-navy">{result.cert.certificateNumber}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-slate-500">Expedido</span>
                  <span className="font-medium text-navy">{fmtDate(result.cert.issuedAt)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Vigente hasta</span>
                  <span className="font-medium text-navy">{fmtDate(result.cert.expiresAt)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400">
          © {new Date().getFullYear()} UBSafety — Todos los derechos reservados
        </p>
      </div>
    </div>
  )
}
